import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtDate } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

export function ReserveSlotDialog({ open, onClose, onSaved, isAdmin, team, user, defaultDate }) {
  const [form, setForm] = useState({ date: defaultDate || '', time: '', patient_name: '', nutri_id: user?.user_id || '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/schedule-holds`, { ...form, date: form.date || defaultDate });
      toast.success('Horário reservado por 24 horas');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Não foi possível reservar');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl border-brand-border" data-testid="reserve-dialog">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Reservar horário</DialogTitle></DialogHeader>
        <p className="text-xs text-brand-muted">Enquanto estiver ativa, o horário deixa de aparecer no link de marcação. Expira ao fim de 24 horas.</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Data</Label><Input type="date" value={form.date || defaultDate || ''} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl" data-testid="reserve-date" /></div>
          <div><Label className="text-xs">Hora</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="rounded-xl" data-testid="reserve-time" /></div>
        </div>
        <div><Label className="text-xs">Nome da paciente</Label><Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} className="rounded-xl" data-testid="reserve-patient-name" /></div>
        {isAdmin && (
          <div><Label className="text-xs">Nutricionista</Label>
            <select value={form.nutri_id} onChange={e => setForm({ ...form, nutri_id: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2 text-sm bg-white" data-testid="reserve-nutri">
              <option value={user?.user_id}>{user?.name || 'Eu'}</option>
              {(team || []).filter(t => t.user_id !== user?.user_id).map(t => <option key={t.user_id} value={t.user_id}>{t.name || t.email}</option>)}
            </select>
          </div>
        )}
        <button onClick={save} disabled={saving || !form.time || !form.patient_name.trim() || !(form.date || defaultDate)} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40" data-testid="reserve-save-btn">{saving ? 'A reservar...' : 'Reservar'}</button>
      </DialogContent>
    </Dialog>
  );
}

export function HoldDetailDialog({ hold, clients, onClose, onChanged }) {
  const [patientId, setPatientId] = useState(hold?.patient_id || '');
  const [busy, setBusy] = useState(false);
  if (!hold) return null;
  const isPayment = hold.kind === 'payment';
  const convert = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/admin/schedule-holds/${hold.id}/convert`, { patient_id: patientId }); toast.success('Reserva convertida em consulta'); onChanged(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Erro ao converter'); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm('Tem a certeza?')) return;
    try { await axios.delete(`${API}/api/admin/schedule-holds/${hold.id}`); toast.success('Reserva cancelada'); onChanged(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Erro ao cancelar'); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-3xl border-brand-border" data-testid="hold-dialog">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">{isPayment ? 'Reserva (pagamento por associar)' : 'Reserva provisória'}</DialogTitle></DialogHeader>
        <p className="text-sm text-brand-text">{hold.patient_name} · {fmtDate(hold.date)} às {hold.time}</p>
        {isPayment ? (
          <p className="text-xs text-brand-muted" data-testid="hold-payment-note">Reserva ligada a um pagamento em Financeiro, Pagamentos por associar. Associa ou liberta o pagamento lá.</p>
        ) : (
          <>
            <p className="text-xs text-brand-muted">Expira em {hold.expires_at ? new Date(hold.expires_at).toLocaleString('pt-PT') : '-'}.</p>
            <div><Label className="text-xs">Converter em consulta (escolhe a paciente)</Label>
              <select value={patientId} onChange={e => setPatientId(e.target.value)} className="w-full rounded-xl border border-brand-border px-3 py-2 text-sm bg-white" data-testid="hold-patient-select">
                <option value="">Escolher paciente...</option>
                {(clients || []).map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={convert} disabled={busy || !patientId} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-40" data-testid="hold-convert-btn">Converter em consulta</button>
              <button onClick={cancel} className="flex-1 border border-red-300 text-red-600 rounded-full py-2.5 text-sm" data-testid="hold-cancel-btn">Cancelar reserva</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
